import { getIntl, getMessages } from 'locales';
import type { ProductMetadata } from 'models/batch/import-metadata';
import { isNbOfBatchFormWithinThreshold } from 'state/batchForm/infos/infos.helpers';
import { getUserPreferencesLanguage } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { batchWorkspaceTabProductSelectionSubmittedThunk } from 'state/workspaces/batch/tabContent/tabContent.thunks';
import { workspaceTabCreated } from 'state/workspaces/workspace.actions';
import { parseExcel } from 'utils/import/excelExtractor';
import { buildImportRequests, type FileParseResult } from 'utils/import/utils/parseFileUtils';
import {
  createProductReverseMap,
  createProductRows,
  getUniqueProductVariations,
} from 'utils/import/utils/productMappingUtils';
import { getProductLabel } from 'utils/lang/lang';
import { getToastUtils } from 'utils/toast';

import { batchImportFormRequested, batchImportMetadataRequested } from '../batchForm.action';

export const QUOTE_IMPORT_FAILED_ID = 'quote.import.batchWorkspace.failed';
export const QUOTE_IMPORT_BUTTON_ID = 'quote.import.button';

function createProductVariationKey(productType: string, productSubType: string, wrapper: string): string {
  return `${productType}-${productSubType}-${wrapper}`;
}

function createVariationTabMap(
  fileParsedResult: FileParseResult,
  dispatch: Parameters<Thunk<void>>[0],
  getNewGuid: () => string,
) {
  const intl = getIntl(fileParsedResult.locale);
  const importLiteral = intl.formatMessage({ id: 'batchPricer.quote.import' });
  const variationTabMap = new Map<string, string>();

  getUniqueProductVariations(fileParsedResult.rows).forEach(({ productType, productSubType, wrapper }) => {
    const tabId = getNewGuid();
    variationTabMap.set(createProductVariationKey(productType, productSubType, wrapper), tabId);

    dispatch(workspaceTabCreated(tabId, 'newBatch', 'import'));
    dispatch(
      batchWorkspaceTabProductSelectionSubmittedThunk(
        tabId,
        productType,
        productSubType,
        wrapper,
        'RequestForQuote',
        undefined,
        `${getProductLabel(intl, productSubType)} - ${importLiteral}`,
        true,
      ),
    );
  });

  return variationTabMap;
}

export function importExcelToBatchWorkspaceThunk(buffer: ArrayBuffer): Thunk<void> {
  return (dispatch, getState) => {
    const toast = getToastUtils(dispatch);

    try {
      const state = getState();
      const locale = getUserPreferencesLanguage(state);
      const { dataMaps, errors } = parseExcel(buffer);
      if (errors.length > 0 || dataMaps.length === 0) {
        toast.error({ id: QUOTE_IMPORT_FAILED_ID }, { id: QUOTE_IMPORT_BUTTON_ID });
        return;
      }
      if (!isNbOfBatchFormWithinThreshold(dataMaps.length)) {
        toast.error(
          { id: 'quote.import.tooManyRows', values: { nbOfQuotes: dataMaps.length } },
          { id: QUOTE_IMPORT_BUTTON_ID },
        );
        return;
      }
      const translationMap = getMessages(locale);
      const productReverseMap = createProductReverseMap(translationMap);
      const rows = createProductRows(dataMaps, translationMap, productReverseMap);

      if (rows.some(row => !row.productType || !row.productSubType || !row.wrapper)) {
        toast.error({ id: QUOTE_IMPORT_FAILED_ID }, { id: QUOTE_IMPORT_BUTTON_ID });
        return;
      }

      toast.success(
        {
          id: 'quote.import.batchWorkspace.requested',
          values: { nbOfQuotes: dataMaps.length },
        },
        { id: 'toast.import.inProgress' },
      );
      dispatch(batchImportMetadataRequested({ locale, rows, errors: [] }));
    } catch {
      toast.error({ id: QUOTE_IMPORT_FAILED_ID }, { id: QUOTE_IMPORT_BUTTON_ID });
    }
  };
}

export function batchImportMetadataReceivedThunk(
  fileParsedResult: FileParseResult,
  productsMetadata: ProductMetadata[],
): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const toast = getToastUtils(dispatch);
    getState();

    const requests = buildImportRequests(fileParsedResult, productsMetadata);
    const validationErrors = requests.flatMap(request => request.validationErrors ?? []);

    if (validationErrors.length > 0) {
      validationErrors.forEach(error => {
        toast.error(
          {
            id: 'importFileValidator.fieldValueInvalid',
            values: { row: error.row, field: error.fieldId, errorMsg: error.errorMsg },
          },
          { id: QUOTE_IMPORT_BUTTON_ID },
          false,
        );
      });
      return;
    }

    const variationTabMap = createVariationTabMap(fileParsedResult, dispatch, getNewGuid);

    requests.forEach(request => {
      const tabId = variationTabMap.get(
        createProductVariationKey(request.productType, request.productSubType, request.wrapper),
      );
      if (!tabId) {
        return;
      }

      const temporaryFormId = getNewGuid();
      dispatch(
        batchImportFormRequested(
          tabId,
          temporaryFormId,
          request.productType,
          request.productSubType,
          request.wrapper,
          request,
        ),
      );
    });
  };
}
